import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

type SupportTicketComment = {
  authorUserId?: string;
  authorName?: string;
  body: string;
  visibility: "internal" | "customer";
  createdAt: string;
};

type SearchableUser = {
  id: string;
  uid?: string;
  name: string;
  displayName?: string;
  email: string;
  photoURL?: string;
  role?: string;
};

type SupportTicket = {
  _id: string;
  ticketNumber: string;
  requesterUserId?: string;
  requesterName: string;
  requesterPhone: string;
  requesterEmail?: string;
  category: string;
  subcategory?: string;
  priority: string;
  status: string;
  subject: string;
  description: string;
  source: string;
  tags: string[];
  assignedToUserId?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  assignedToPhotoURL?: string;
  assignedTeam?: string;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  resolutionSummary?: string;
  comments?: SupportTicketComment[];
  createdAt: string;
  updatedAt: string;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1/support/tickets`;
const USER_DIRECTORY_API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/user-directory`;
const cardsBorderRadii = "8px";

const categoryColorScheme = (category?: string) => {
  switch (category) {
    case "payment":
      return "red";
    case "delivery":
      return "orange";
    case "bookings":
      return "purple";
    case "orders":
      return "blue";
    case "technical":
      return "pink";
    case "account":
      return "cyan";
    default:
      return "gray";
  }
};

const priorityColorScheme = (priority?: string) => {
  switch (priority) {
    case "urgent":
      return "red";
    case "high":
      return "orange";
    case "normal":
      return "blue";
    case "low":
      return "gray";
    default:
      return "gray";
  }
};

const statusColorScheme = (status?: string) => {
  switch (status) {
    case "open":
      return "red";
    case "assigned":
      return "orange";
    case "in_progress":
      return "blue";
    case "waiting_on_customer":
      return "yellow";
    case "resolved":
      return "green";
    case "closed":
      return "gray";
    default:
      return "gray";
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const SupportTicketDetailsPage: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);

  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [assignedToName, setAssignedToName] = useState("");
  const [assignedToEmail, setAssignedToEmail] = useState("");
  const [assignedToPhotoURL, setAssignedToPhotoURL] = useState("");
  const [assignedTeam, setAssignedTeam] = useState("");

  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState<SearchableUser[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [resolutionSummary, setResolutionSummary] = useState("");
  const [commentBody, setCommentBody] = useState("");

  const [assignmentError, setAssignmentError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [priorityError, setPriorityError] = useState("");
  const [resolutionError, setResolutionError] = useState("");
  const [commentError, setCommentError] = useState("");

  const [commentVisibility, setCommentVisibility] = useState<
    "internal" | "customer"
  >("internal");

  const actorUserId = user?.uid || "";

  const fetchTicket = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/${id}`);
      const nextTicket = res.data?.ticket || null;
      setTicket(nextTicket);

      if (nextTicket) {
        setAssignedToUserId(nextTicket.assignedToUserId || "");
        setAssignedToName(nextTicket.assignedToName || "");
        setAssignedToEmail(nextTicket.assignedToEmail || "");
        setAssignedToPhotoURL(nextTicket.assignedToPhotoURL || "");
        setAssignedTeam(nextTicket.assignedTeam || "");
        setStatus(nextTicket.status || "");
        setPriority(nextTicket.priority || "");
        setResolutionSummary(nextTicket.resolutionSummary || "");
      }
    } catch (error) {
      console.error("Failed to fetch support ticket:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    const query = userSearch.trim();

    if (query.length < 2) {
      setUserResults([]);
      setUserSearchOpen(false);
      setSearchingUsers(false);
      setUserSearchError("");
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setSearchingUsers(true);
      setUserSearchError("");

      try {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
          throw new Error("You must be signed in to search users.");
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${USER_DIRECTORY_API}?search=${encodeURIComponent(query)}&limit=20`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Users could not be searched.",
          );
        }

        const users = Array.isArray(payload?.users)
          ? payload.users
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const normalizedUsers: SearchableUser[] = users
          .map((candidate: Record<string, unknown>) => {
            const uid = String(
              candidate.uid || candidate.id || candidate.firebaseUid || "",
            ).trim();

            const name = String(
              candidate.displayName ||
                candidate.name ||
                candidate.email ||
                uid ||
                "Unnamed user",
            ).trim();

            return {
              id: uid,
              uid,
              name,
              displayName: name,
              email: String(candidate.email || "").trim(),
              photoURL: candidate.photoURL
                ? String(candidate.photoURL).trim()
                : undefined,
              role: candidate.role ? String(candidate.role) : undefined,
            };
          })
          .filter((candidate: SearchableUser) => Boolean(candidate.id));

        setUserResults(normalizedUsers);
        setUserSearchOpen(true);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.error("Failed to search users:", error);
        setUserResults([]);
        setUserSearchOpen(true);
        setUserSearchError(
          error instanceof Error ? error.message : "Unable to search users.",
        );
      } finally {
        setSearchingUsers(false);
      }
    }, 280);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [userSearch]);

  const handleAssign = async (assignee?: SearchableUser) => {
    if (!ticket) return;

    const nextUserId = String(assignee?.id || assignedToUserId || "").trim();
    const nextName = String(
      assignee?.displayName || assignee?.name || assignedToName || "",
    ).trim();
    const nextEmail = String(assignee?.email || assignedToEmail || "")
      .trim()
      .toLowerCase();
    const nextPhotoURL = String(
      assignee?.photoURL || assignedToPhotoURL || "",
    ).trim();

    if (!nextUserId) {
      const message =
        "Search by name or email and select who should own this ticket.";
      setAssignmentError(message);
      toast({
        title: "Choose a user first",
        description: message,
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/${ticket._id}/assign`, {
        assignedToUserId: nextUserId,
        assignedToName: nextName,
        assignedToEmail: nextEmail,
        assignedToPhotoURL: nextPhotoURL,
        assignedTeam,
        actorUserId,
      });

      const nextTicket = res.data?.ticket || ticket;

      setTicket(nextTicket);
      setAssignedToUserId(nextTicket.assignedToUserId || nextUserId);
      setAssignedToName(nextTicket.assignedToName || nextName);
      setAssignedToEmail(nextTicket.assignedToEmail || nextEmail);
      setAssignedToPhotoURL(nextTicket.assignedToPhotoURL || nextPhotoURL);
      setStatus(nextTicket.status || "assigned");
      setUserSearch("");
      setUserResults([]);
      setUserSearchOpen(false);
      setUserSearchError("");
      setAssignmentError("");

      toast({
        title: "Ticket assigned",
        description: `${nextName || nextEmail || "The selected user"} now owns ${ticket.ticketNumber}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to assign support ticket:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      setAssignmentError(message);
      toast({
        title: "Assignment failed",
        description: message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!actorUserId) {
      const message = "Please sign in again and retry.";
      setAssignmentError(message);
      toast({
        title: "Unable to identify your account",
        description: message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    await handleAssign({
      id: actorUserId,
      uid: actorUserId,
      name:
        user?.displayName ||
        (user as any)?.name ||
        user?.email ||
        "Support Agent",
      displayName:
        user?.displayName ||
        (user as any)?.name ||
        user?.email ||
        "Support Agent",
      email: user?.email || "",
      photoURL: (user as any)?.photoURL || "",
      role: (user as any)?.role || "",
    });
  };

  const handleStatusUpdate = async () => {
    if (!ticket) return;

    if (!status) {
      const message = "Choose a valid ticket status.";
      setStatusError(message);
      toast({
        title: "Status required",
        description: message,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/${ticket._id}/status`, {
        status,
        actorUserId,
      });
      setTicket(res.data?.ticket || ticket);
      setStatusError("");

      toast({
        title: "Status updated",
        description: `${ticket.ticketNumber} is now ${status.replace(/_/g, " ")}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to update support ticket status:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      setStatusError(message);
      toast({
        title: "Status update failed",
        description: message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePriorityUpdate = async () => {
    if (!ticket) return;

    if (!priority) {
      const message = "Choose a valid ticket priority.";
      setPriorityError(message);
      toast({
        title: "Priority required",
        description: message,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/${ticket._id}/priority`, {
        priority,
      });
      setTicket(res.data?.ticket || ticket);
      setPriorityError("");

      toast({
        title: "Priority updated",
        description: `${ticket.ticketNumber} priority is now ${priority}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to update support ticket priority:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      setPriorityError(message);
      toast({
        title: "Priority update failed",
        description: message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    if (!ticket) return;

    if (!resolutionSummary.trim()) {
      const message =
        "Add a short resolution summary before resolving the ticket.";
      setResolutionError(message);
      toast({
        title: "Resolution summary required",
        description: message,
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/${ticket._id}/resolve`, {
        resolutionSummary,
        actorUserId,
      });
      const nextTicket = res.data?.ticket || ticket;
      setTicket(nextTicket);
      setStatus(nextTicket.status || "resolved");
      setStatusError("");
      setResolutionSummary("");
      setResolutionError("");

      toast({
        title: "Ticket resolved",
        description: `${ticket.ticketNumber} has been resolved successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to resolve support ticket:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      setResolutionError(message);
      toast({
        title: "Resolve failed",
        description: message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (!ticket) return;

    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/${ticket._id}/close`);
      const nextTicket = res.data?.ticket || ticket;
      setTicket(nextTicket);
      setStatus(nextTicket.status || "closed");
      setStatusError("");

      toast({
        title: "Ticket closed",
        description: `${ticket.ticketNumber} has been closed.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to close support ticket:", error);
      toast({
        title: "Close failed",
        description:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!ticket) return;

    if (!commentBody.trim()) {
      const message = "Write a comment before adding it to the ticket.";
      setCommentError(message);
      toast({
        title: "Comment required",
        description: message,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const visibility = commentVisibility;
      const res = await axios.post(`${API_BASE}/${ticket._id}/comments`, {
        authorUserId: actorUserId,
        authorName: user?.displayName || user?.email || "Support Agent",
        body: commentBody,
        visibility,
      });

      setTicket(res.data?.ticket || ticket);
      setCommentBody("");
      setCommentVisibility("internal");
      setCommentError("");

      toast({
        title: "Comment added",
        description:
          visibility === "customer"
            ? "Customer-facing comment added successfully."
            : "Internal comment added successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Failed to add support ticket comment:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      setCommentError(message);
      toast({
        title: "Comment failed",
        description: message,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" py={12} gap={2}>
        <GlobalSpinner />
        <Text color={theme.textSubtle}>Loading ticket...</Text>
      </Flex>
    );
  }

  if (!ticket) {
    return (
      <Box p={6}>
        <VStack align="start" spacing={4}>
          <Text color={theme.text}>Support ticket not found.</Text>
          <Button onClick={() => navigate("/support/tickets")}>
            Back to Tickets
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box w="100%" mx="auto" p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={5}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={theme.shadowSm}
        >
          <Stack
            direction={{ base: "column", lg: "row" }}
            justify="space-between"
            align={{ base: "start", lg: "center" }}
            spacing={4}
          >
            <VStack align="start" spacing={2}>
              <HStack spacing={2} flexWrap="wrap">
                <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                  {ticket.ticketNumber}
                </Text>
                <Badge colorScheme={statusColorScheme(ticket.status)}>
                  {ticket.status}
                </Badge>
                <Badge colorScheme={categoryColorScheme(ticket.category)}>
                  {ticket.category}
                </Badge>
                <Badge colorScheme={priorityColorScheme(ticket.priority)}>
                  {ticket.priority}
                </Badge>
              </HStack>

              <Text color={theme.text} fontWeight="semibold">
                {ticket.subject}
              </Text>
              <Text color={theme.textSubtle}>
                Created: {formatDate(ticket.createdAt)}
              </Text>
            </VStack>

            <Button
              variant="outline"
              borderColor={theme.borderStrong}
              color={theme.text}
              _hover={{ bg: theme.hover }}
              onClick={() => navigate("/support/tickets")}
            >
              Back to Tickets
            </Button>
          </Stack>
        </Box>

        <Stack
          direction={{ base: "column", xl: "row" }}
          spacing={5}
          align="start"
        >
          <VStack flex="1.2" align="stretch" spacing={5}>
            <Box
              border="1px solid"
              borderColor={theme.border}
              bg={theme.surface}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <VStack align="stretch" spacing={4}>
                <Text fontWeight="bold">Requester Details</Text>

                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Name
                    </Text>
                    <Text>{ticket.requesterName}</Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Phone
                    </Text>
                    <Text>{ticket.requesterPhone}</Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Email
                    </Text>
                    <Text>{ticket.requesterEmail || "—"}</Text>
                  </Box>
                </Stack>

                <Box>
                  <Text fontSize="sm" color={theme.textSubtle}>
                    Description
                  </Text>
                  <Text mt={1}>{ticket.description}</Text>
                </Box>

                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Assigned To
                    </Text>
                    {ticket.assignedToUserId ? (
                      <HStack mt={1} spacing={2} align="center">
                        <Avatar
                          size="xs"
                          src={ticket.assignedToPhotoURL}
                          name={
                            ticket.assignedToName ||
                            ticket.assignedToEmail ||
                            ticket.assignedToUserId
                          }
                        />
                        <Box minW={0}>
                          <Text fontWeight="semibold" noOfLines={1}>
                            {ticket.assignedToName ||
                              ticket.assignedToEmail ||
                              ticket.assignedToUserId}
                          </Text>
                          {ticket.assignedToEmail && ticket.assignedToName ? (
                            <Text
                              fontSize="xs"
                              color={theme.textSubtle}
                              noOfLines={1}
                            >
                              {ticket.assignedToEmail}
                            </Text>
                          ) : null}
                        </Box>
                      </HStack>
                    ) : (
                      <Text>Unassigned</Text>
                    )}
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Assigned Team
                    </Text>
                    <Text>{ticket.assignedTeam || "—"}</Text>
                  </Box>
                </Stack>

                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      First Response
                    </Text>
                    <Text>{formatDate(ticket.firstResponseAt)}</Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Resolved At
                    </Text>
                    <Text>{formatDate(ticket.resolvedAt)}</Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Closed At
                    </Text>
                    <Text>{formatDate(ticket.closedAt)}</Text>
                  </Box>
                </Stack>

                <Box>
                  <Text fontSize="sm" color={theme.textSubtle}>
                    Resolution Summary
                  </Text>
                  <Text mt={1}>{ticket.resolutionSummary || "—"}</Text>
                </Box>
              </VStack>
            </Box>

            <Box
              border="1px solid"
              borderColor={theme.border}
              bg={theme.surface}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <VStack align="stretch" spacing={4}>
                <Text fontWeight="bold">Comments</Text>

                {ticket.comments?.length ? (
                  <VStack align="stretch" spacing={3}>
                    {ticket.comments.map((comment, index) => (
                      <Box
                        key={`${comment.createdAt}-${index}`}
                        border="1px solid"
                        borderColor={theme.border}
                        borderRadius={cardsBorderRadii}
                        p={3}
                        bg={theme.surfaceAlt || theme.surface}
                      >
                        <HStack spacing={2} mb={2} flexWrap="wrap">
                          <Text fontWeight="semibold">
                            {comment.authorName ||
                              comment.authorUserId ||
                              "Unknown"}
                          </Text>
                          <Badge
                            colorScheme={
                              comment.visibility === "internal"
                                ? "purple"
                                : "green"
                            }
                          >
                            {comment.visibility}
                          </Badge>
                        </HStack>
                        <Text color={theme.textMuted}>{comment.body}</Text>
                        <Text mt={2} fontSize="sm" color={theme.textSubtle}>
                          {formatDate(comment.createdAt)}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text color={theme.textSubtle}>No comments yet.</Text>
                )}

                <FormControl isRequired isInvalid={Boolean(commentError)}>
                  <FormLabel>Add Comment</FormLabel>
                  <Textarea
                    value={commentBody}
                    onChange={(e) => {
                      setCommentBody(e.target.value);
                      if (commentError) setCommentError("");
                    }}
                    placeholder="Write an internal or customer-facing comment"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                    minH="120px"
                  />
                  <FormErrorMessage>{commentError}</FormErrorMessage>
                </FormControl>

                <HStack flexWrap="wrap">
                  <Select
                    value={commentVisibility}
                    onChange={(e) =>
                      setCommentVisibility(
                        e.target.value as "internal" | "customer",
                      )
                    }
                    maxW="220px"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  >
                    <option value="internal">Internal</option>
                    <option value="customer">Customer</option>
                  </Select>

                  <Button
                    onClick={handleAddComment}
                    isLoading={saving}
                    color={theme.textOnPrimary}
                    bgImage={theme.gradients.primaryButton}
                    _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                  >
                    Add Comment
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>

          <VStack
            flex="0.8"
            align="stretch"
            spacing={5}
            w={{ base: "100%", xl: "420px" }}
          >
            <Box
              border="1px solid"
              borderColor={theme.border}
              bg={theme.surface}
              borderRadius={cardsBorderRadii}
              p={{ base: 4, md: 5 }}
              boxShadow={theme.shadowSm}
            >
              <VStack align="stretch" spacing={4}>
                <Text fontWeight="bold">Manage Ticket</Text>

                <Box
                  border="1px solid"
                  borderColor={theme.border}
                  bg={theme.surfaceAlt || theme.surface}
                  borderRadius={cardsBorderRadii}
                  p={3}
                >
                  <HStack justify="space-between" align="center" spacing={3}>
                    <HStack spacing={3} minW={0}>
                      <Avatar
                        size="sm"
                        src={assignedToPhotoURL}
                        name={
                          assignedToName ||
                          assignedToEmail ||
                          assignedToUserId ||
                          "Unassigned"
                        }
                      />
                      <Box minW={0}>
                        <Text
                          fontSize="xs"
                          color={theme.textSubtle}
                          textTransform="uppercase"
                          fontWeight="bold"
                        >
                          Current assignee
                        </Text>
                        <Text fontWeight="semibold" noOfLines={1}>
                          {assignedToName ||
                            assignedToEmail ||
                            assignedToUserId ||
                            "Unassigned"}
                        </Text>
                        {assignedToEmail && assignedToName ? (
                          <Text
                            fontSize="xs"
                            color={theme.textSubtle}
                            noOfLines={1}
                          >
                            {assignedToEmail}
                          </Text>
                        ) : null}
                      </Box>
                    </HStack>

                    {actorUserId ? (
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                        onClick={handleAssignToMe}
                        isDisabled={saving || assignedToUserId === actorUserId}
                      >
                        Assign to me
                      </Button>
                    ) : null}
                  </HStack>
                </Box>

                <FormControl
                  isInvalid={Boolean(assignmentError || userSearchError)}
                >
                  <FormLabel mb={1}>Find a user</FormLabel>
                  <Text fontSize="xs" color={theme.textSubtle} mb={2}>
                    Search by name or email, then click a person to assign the
                    ticket.
                  </Text>

                  <Box position="relative">
                    <Input
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setUserSearchOpen(e.target.value.trim().length >= 2);
                        if (assignmentError) setAssignmentError("");
                        if (userSearchError) setUserSearchError("");
                      }}
                      onFocus={() => {
                        if (userSearch.trim().length >= 2) {
                          setUserSearchOpen(true);
                        }
                      }}
                      onBlur={() => {
                        window.setTimeout(() => setUserSearchOpen(false), 150);
                      }}
                      placeholder="Start typing a name or email..."
                      bg={theme.inputBg}
                      borderColor={theme.inputBorder}
                      pr="42px"
                      autoComplete="off"
                    />

                    {searchingUsers ? (
                      <Flex
                        position="absolute"
                        right="12px"
                        top="0"
                        bottom="0"
                        align="center"
                        pointerEvents="none"
                      >
                        <Spinner size="sm" />
                      </Flex>
                    ) : null}

                    {userSearchOpen && userSearch.trim().length >= 2 ? (
                      <Box
                        position="absolute"
                        top="calc(100% + 6px)"
                        left={0}
                        right={0}
                        zIndex={40}
                        border="1px solid"
                        borderColor={theme.border}
                        bg={theme.surface}
                        borderRadius={cardsBorderRadii}
                        boxShadow={theme.shadowMd}
                        overflow="hidden"
                        maxH="320px"
                        overflowY="auto"
                      >
                        {userSearchError ? (
                          <Text p={3} fontSize="sm" color="red.400">
                            {userSearchError}
                          </Text>
                        ) : !searchingUsers && userResults.length === 0 ? (
                          <Text p={3} fontSize="sm" color={theme.textSubtle}>
                            No users match “{userSearch.trim()}”.
                          </Text>
                        ) : (
                          userResults.map((candidate) => (
                            <Button
                              key={candidate.id}
                              variant="ghost"
                              w="100%"
                              h="auto"
                              borderRadius="0"
                              justifyContent="flex-start"
                              px={3}
                              py={3}
                              borderBottom="1px solid"
                              borderColor={theme.border}
                              _last={{ borderBottom: "none" }}
                              _hover={{ bg: theme.hover }}
                              onClick={() => handleAssign(candidate)}
                              isDisabled={saving}
                            >
                              <HStack w="100%" spacing={3} textAlign="left">
                                <Avatar
                                  size="sm"
                                  src={candidate.photoURL}
                                  name={candidate.name}
                                />

                                <Box flex="1" minW={0}>
                                  <HStack spacing={2} flexWrap="wrap">
                                    <Text
                                      fontWeight="semibold"
                                      color={theme.text}
                                      noOfLines={1}
                                    >
                                      {candidate.name}
                                    </Text>
                                    {candidate.role ? (
                                      <Badge
                                        colorScheme={
                                          candidate.role === "superadmin"
                                            ? "purple"
                                            : candidate.role === "admin"
                                              ? "blue"
                                              : "gray"
                                        }
                                      >
                                        {candidate.role}
                                      </Badge>
                                    ) : null}
                                  </HStack>
                                  <Text
                                    fontSize="xs"
                                    color={theme.textSubtle}
                                    noOfLines={1}
                                  >
                                    {candidate.email || candidate.id}
                                  </Text>
                                </Box>

                                <Text
                                  fontSize="sm"
                                  fontWeight="bold"
                                  color={theme.primary}
                                >
                                  Assign
                                </Text>
                              </HStack>
                            </Button>
                          ))
                        )}
                      </Box>
                    ) : null}
                  </Box>
                  <FormErrorMessage>
                    {assignmentError || userSearchError}
                  </FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Assign team</FormLabel>
                  <Input
                    value={assignedTeam}
                    onChange={(e) => setAssignedTeam(e.target.value)}
                    placeholder="Support team (optional)"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  />
                </FormControl>

                <Button
                  onClick={() => handleAssign()}
                  isLoading={saving}
                  isDisabled={!assignedToUserId}
                >
                  Save Assignment Details
                </Button>

                <FormControl isRequired isInvalid={Boolean(statusError)}>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      if (statusError) setStatusError("");
                    }}
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  >
                    <option value="open">Open</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting_on_customer">
                      Waiting on Customer
                    </option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </Select>
                  <FormErrorMessage>{statusError}</FormErrorMessage>
                </FormControl>

                <Button onClick={handleStatusUpdate} isLoading={saving}>
                  Update Status
                </Button>

                <FormControl isRequired isInvalid={Boolean(priorityError)}>
                  <FormLabel>Priority</FormLabel>
                  <Select
                    value={priority}
                    onChange={(e) => {
                      setPriority(e.target.value);
                      if (priorityError) setPriorityError("");
                    }}
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Select>
                  <FormErrorMessage>{priorityError}</FormErrorMessage>
                </FormControl>

                <Button onClick={handlePriorityUpdate} isLoading={saving}>
                  Update Priority
                </Button>

                <FormControl isRequired isInvalid={Boolean(resolutionError)}>
                  <FormLabel>Resolution Summary</FormLabel>
                  <Textarea
                    value={resolutionSummary}
                    onChange={(e) => {
                      setResolutionSummary(e.target.value);
                      if (resolutionError) setResolutionError("");
                    }}
                    placeholder="Write a resolution summary"
                    bg={theme.inputBg}
                    borderColor={theme.inputBorder}
                    minH="120px"
                  />
                  <FormErrorMessage>{resolutionError}</FormErrorMessage>
                </FormControl>

                <HStack flexWrap="wrap">
                  <Button
                    onClick={handleResolve}
                    isLoading={saving}
                    colorScheme="green"
                  >
                    Resolve
                  </Button>
                  <Button
                    onClick={handleClose}
                    isLoading={saving}
                    variant="outline"
                  >
                    Close
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </Stack>
      </VStack>
    </Box>
  );
};

export default SupportTicketDetailsPage;
